import { PMSDevice } from "../_models/PMSDevice";
import { PMSRoom } from "../_models/PMSRoom";
import { PMSLocationInfo } from "../_models/PMSLocationInfo";
import { DeviceHelper } from "../../Everou/_helpers";
import { TimeHelper } from "../../Everou/_helpers/TimeHelper";
import { Device } from "../../Everou/_models/Device";
import { infoCellForDeviceType } from "../../Everou/_constants";
import { LogsHelper } from "../../Everou/_helpers";

// Which local field each overridable setting is called on the server. Shared
// with the actions: an optimistic write has to mark the field as the
// location's own, and it only knows the server's name for it.
export const PMS_LOCATION_SETTING_FIELDS = {
    invitationLinkAddToHome: "invitation_link_add_to_home",
    invitationLinkAppName: "invitation_link_app_name",
    invitationLinkLocationCheckMode: "invitation_link_location_check_mode",
    invitationLinkLocationRadius: "invitation_link_location_radius",
    invitationLinkHideHeader: "invitation_link_hide_header",
    invitationLinkHideFooter: "invitation_link_hide_footer",
    urlRecommendations: "url_recommendations",
    videoUrl: "video_url",
    externalVideoUrl: "external_video_url",
    earlyCheckinEnabled: "early_checkin_enabled",
    earlyCheckoutEnabled: "early_checkout_enabled",
    protectAutoEnabledWithoutReservations:
        "protect_auto_enabled_without_reservations",
    enableProtectGuest: "enable_protect_guest",
    securityAlertTime: "security_alert_time",
};

export class PMSLocationInfoMapper {
    static mapServerToLocal(serverLocationInfo) {
        const {
            timezone,
            name,
            loc_id,
            protect,
            protect_auto_enabled_without_reservations,
            rooms,
            video_name,
            video_url,
            loc_uid,
            url_recommendations,
            traveler_discount,
            external_video_url,
            public_device_overrides,
            lat,
            long,
            address,
        } = serverLocationInfo;

        const {
            has_customization,
            has_protect,
            has_protect_subscription,
            vacation_rental_plan,
        } = serverLocationInfo.features;

        // `/location/info/new` may expose these either at the root or inside `features`.
        const early_checkin_enabled =
            serverLocationInfo.early_checkin_enabled
            ?? serverLocationInfo.features?.early_checkin_enabled
            ?? false;
        const early_checkout_enabled =
            serverLocationInfo.early_checkout_enabled
            ?? serverLocationInfo.features?.early_checkout_enabled
            ?? false;
        const invitation_link_add_to_home =
            serverLocationInfo.invitation_link_add_to_home
            ?? serverLocationInfo.features?.invitation_link_add_to_home
            ?? false;
        const invitation_link_app_name =
            serverLocationInfo.invitation_link_app_name
            ?? serverLocationInfo.features?.invitation_link_app_name
            ?? "";
        // Hide the access link's own header (name, dates, timezone) and footer
        // (icon navbar) for hosts who embed it in their site.
        const invitation_link_hide_header =
            serverLocationInfo.invitation_link_hide_header
            ?? serverLocationInfo.features?.invitation_link_hide_header
            ?? false;
        const invitation_link_hide_footer =
            serverLocationInfo.invitation_link_hide_footer
            ?? serverLocationInfo.features?.invitation_link_hide_footer
            ?? false;
        // Premium-only: skip the guest geolocation check on the public link.
        const invitation_link_disable_location_check =
            serverLocationInfo.invitation_link_disable_location_check
            ?? serverLocationInfo.features?.invitation_link_disable_location_check
            ?? false;
        // How strict the check is: "off" | "warn" | "required". The boolean above is
        // the old two-state view of this same setting, kept by the API.
        const invitation_link_location_check_mode =
            serverLocationInfo.invitation_link_location_check_mode
            ?? serverLocationInfo.features?.invitation_link_location_check_mode
            ?? (invitation_link_disable_location_check ? "off" : "warn");
        const invitation_link_location_radius =
            serverLocationInfo.invitation_link_location_radius
            ?? serverLocationInfo.features?.invitation_link_location_radius
            ?? 200;
        // Guest-facing alarm button. May live at the root or inside `features`.
        const enable_protect_guest =
            serverLocationInfo.enable_protect_guest
            ?? serverLocationInfo.features?.enable_protect_guest
            ?? false;
        // Seconds. 0 = disabled, >0 = active. Threshold is per-location.
        const security_alert_time =
            serverLocationInfo.security_alert_time
            ?? serverLocationInfo.features?.security_alert_time
            ?? 0;

        // Effective values above are already merged by the API. own_* tells
        // the UI which values the location overrode, just like own_* in the
        // customization response. During a rolling deployment the metadata
        // may be absent altogether; null keeps the old UI behaviour in that
        // case instead of falsely labelling every value as inherited.
        const settingFields = PMS_LOCATION_SETTING_FIELDS;
        const groupedOwnSettings = serverLocationInfo.own_settings;
        const hasOwnSettingsMetadata =
            groupedOwnSettings !== undefined ||
            serverLocationInfo.settings_organization_name !== undefined ||
            Object.values(settingFields).some((field) =>
                Object.prototype.hasOwnProperty.call(
                    serverLocationInfo,
                    `own_${field}`,
                ),
            );
        const ownSettings = hasOwnSettingsMetadata
            ? Object.entries(settingFields).reduce(
                  (result, [localField, serverField]) => {
                      const source = groupedOwnSettings ?? serverLocationInfo;
                      const sourceField = groupedOwnSettings
                          ? serverField
                          : `own_${serverField}`;
                      if (
                          Object.prototype.hasOwnProperty.call(
                              source,
                              sourceField,
                          ) &&
                          source[sourceField] !== null &&
                          source[sourceField] !== undefined
                      ) {
                          result[localField] = source[sourceField];
                      }
                      return result;
                  },
                  {},
              )
            : null;

        const {
            invitations_allowed,
            invitations_left,
            can_create_superguest
        } = serverLocationInfo.invitation_settings;

        const {
            permission_type,
            notification
        } = serverLocationInfo.user_settings;

        return PMSLocationInfo({
            timezone: timezoneServerToLocal(timezone),
            name: name,
            locationId: loc_id,
            isProtectOn: protect,
            protectAutoEnabledWithoutReservations:protect_auto_enabled_without_reservations,
            rooms: roomsServerToLocal(rooms),
            uid: loc_uid,
            allowsCustomization: has_customization,
            allowsProtect: has_protect,
            hasProtectSubscription: has_protect_subscription,
            invitationsAllowed: invitations_allowed,
            invitationsLeft: invitations_left,
            canCreateSuperguest: can_create_superguest,
            permission: permission_type,
            isNotificationsActive: notification,
            vacationRentalPlan: vacation_rental_plan,
            urlRecommendations: url_recommendations,
            travelerDiscount: traveler_discount,
            videoName: video_name,
            videoUrl: video_url,
            externalVideoUrl: external_video_url,
            publicDeviceOverrides: (public_device_overrides ?? []).map(o => ({
                deviceUid: o.device_uid,
                mode: o.mode,
            })),
            invitationLinkAddToHome: invitation_link_add_to_home ?? false,
            invitationLinkAppName: invitation_link_app_name ?? "",
            invitationLinkHideHeader: invitation_link_hide_header,
            invitationLinkHideFooter: invitation_link_hide_footer,
            invitationLinkDisableLocationCheck: invitation_link_disable_location_check,
            invitationLinkLocationCheckMode: invitation_link_location_check_mode,
            invitationLinkLocationRadius: invitation_link_location_radius,
            latitude: serverToLocalCoordinate(lat),
            longitude: serverToLocalCoordinate(long),
            address: address ?? "",
            earlyCheckinEnabled: early_checkin_enabled,
            earlyCheckoutEnabled: early_checkout_enabled,
            enableProtectGuest: enable_protect_guest,
            securityAlertTime: security_alert_time,
            ownSettings,
            settingsOrganizationName:
                serverLocationInfo.settings_organization_name ?? null,
        });

        function timezoneServerToLocal(timezone) {
            if (timezone === null)
                return "UTC";

            return timezone;
        }

        // -180 is how the backend spells "this location has no coordinates"
        // (same translation as LocationInfoMapper for this very field): left
        // raw it reads as a real point in the middle of the Pacific.
        function serverToLocalCoordinate(coordinate) {
            if (coordinate === -180 || coordinate === undefined)
                return null;

            return coordinate;
        }

        function roomsServerToLocal(rooms) {
            return rooms.map(({
                uid,
                name,
                type,
                devices
            }) => PMSRoom ({
                uid,
                name,
                type,
                devicesAmount: devices?.length ?? 0
            }));
        }
    }

    static serverLocationInfoToLocalDevices(serverLocationInfo) {
        const localDevices = [];
        const locationName = serverLocationInfo.name;
        serverLocationInfo.rooms.forEach(room => {
            const roomName = room.name;
            localDevices.push(
                ...room.devices
                .map(serverDevice =>
                    PMSLocationInfoMapper.deviceServerToLocal(
                        serverDevice,
                        locationName,
                        roomName
                    )
                )
            );
        });
        return localDevices;
    }

    static deviceServerToLocal(serverDevice, locationName, roomName) {
        return Device({
            locationName: locationName,
            room_name: roomName,
            bluetoothId: serverDevice.bluetooth_id,
            battery: serverDevice.battery,
            uid: serverDevice.uid,
            description: serverDevice.desc,
            loc_uid: serverDevice.loc_uid,
            room_uid: serverDevice.room_uid,
            kit_uid: serverDevice.kit_uid,
            status: statusServerToLocal(serverDevice.status),
            type: serverDevice.type,
            timer: serverDevice.timer,
            update: serverDevice.update,
            original_type: serverDevice.original_type,
            version: serverDevice.version,
            lastActivity: serverDevice.lastActivity  ? TimeHelper.serverStringToIsoString(serverDevice.last_activity) : null,
            remote: serverDevice.remote,
            isNfcCardsSynchronized: serverDevice.synchronized_nfc_cards,
            isCalibrated: serverDevice.calibrated,
            notification: serverDevice.notification,
            wlan_ssid: DeviceHelper.getWLanSSIDForDevice(serverDevice.type, serverDevice.network_parameters),
            wifi_rssi: DeviceHelper.getWifiRssiForDevice(serverDevice.type, serverDevice.network_parameters),
            wlan_status: DeviceHelper.getWLanStatusForDevice(serverDevice.type, serverDevice.network_parameters),
            eth_status: DeviceHelper.getEthernetStatusForDevice(serverDevice.type, serverDevice.network_parameters),
            conn_status: DeviceHelper.getConnectionStatusForDevice(serverDevice.type, serverDevice.network_parameters),
            versionWifi: serverDevice.version_wifi,
            versionBle: serverDevice.version_ble,
            cellType: infoCellForDeviceType(serverDevice.type),
            localStatus: DeviceHelper.getLocalStatusForDevice(
                serverDevice.type,
                serverDevice.status,
            ),
            statusLabel: LogsHelper.getStatusLabelFromDevice(serverDevice.type, statusServerToLocal(serverDevice.status)),
            isPublicEligible: serverDevice.is_public_eligible ?? false,
            supportsCodeOnlyMode: serverDevice.supports_code_only_mode ?? false,
        });

        function statusServerToLocal(serverStatus) {
            let localStatus = serverStatus;
            if (localStatus === null)
                localStatus = 0;
        
            return localStatus;
        }
    }
    
}
